import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArtifactForm } from "../../components/ArtifactTable";
import { MODULES } from "../../config/modules";
import { Plus, Pencil, Trash2, GitPullRequestArrow, X } from "lucide-react";

const TYPE = "risk_issue_log";
const HML = { High: 5, Medium: 3, Low: 1 };
const score = (item, key, hmlKey) => {
  const s = parseInt(item[key], 10);
  if (s >= 1 && s <= 5) return s;
  return HML[item[hmlKey]] || 3;
};
const cellColor = (product) => {
  if (product >= 15) return "bg-red-500/80 text-white";
  if (product >= 8) return "bg-amber-400/80 text-neutral-900";
  if (product >= 4) return "bg-yellow-200 text-neutral-900";
  return "bg-emerald-200 text-neutral-900";
};

export default function RiskIssuePage() {
  const { projectId } = useParams();
  const config = MODULES[TYPE];
  const [items, setItems] = useState([]);
  const [cellFilter, setCellFilter] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/${TYPE}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openItems = useMemo(() => items.filter((i) => i.status !== "Closed"), [items]);
  const grid = useMemo(() => {
    const g = {};
    openItems.forEach((i) => {
      const key = `${score(i, "likelihoodScore", "likelihood")}-${score(i, "impactScore", "impact")}`;
      (g[key] = g[key] || []).push(i);
    });
    return g;
  }, [openItems]);

  const visible = useMemo(() => {
    if (!cellFilter) return items;
    return items.filter((i) => `${score(i, "likelihoodScore", "likelihood")}-${score(i, "impactScore", "impact")}` === cellFilter && i.status !== "Closed");
  }, [items, cellFilter]);

  const save = async () => {
    try {
      if (editing) await api.put(`/projects/${projectId}/artifacts/${TYPE}/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/artifacts/${TYPE}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (item) => {
    try { await api.delete(`/projects/${projectId}/artifacts/${TYPE}/${item.id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async (item) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/issues/${item.id}/convert-to-cr`);
      toast.success(`Promoted to ${data.crNumber}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="risk-issue-page">
      <PageHeader overline="Delivery · Risk Management" title="Risk & Issue Log"
        actions={<Button data-testid="add-risk-issue-btn" className="rounded-none"
          onClick={() => { setEditing(null); setForm({ type: "Risk", status: "Open", impact: "Medium", likelihood: "Medium" }); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Add Entry</Button>} />

      <div className="grid lg:grid-cols-[auto_1fr] gap-6 mt-3 items-start">
        <div className="border border-border bg-card p-4" data-testid="risk-heatmap">
          <div className="overline-label mb-3">Heat map — open items (probability × impact)</div>
          <div className="flex">
            <div className="flex flex-col justify-between py-6 pr-2 text-[9px] uppercase tracking-widest text-muted-foreground" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
              Likelihood →
            </div>
            <div>
              {[5, 4, 3, 2, 1].map((l) => (
                <div key={l} className="flex items-center">
                  <span className="w-4 text-[10px] text-muted-foreground text-right mr-1">{l}</span>
                  {[1, 2, 3, 4, 5].map((imp) => {
                    const key = `${l}-${imp}`;
                    const count = (grid[key] || []).length;
                    const active = cellFilter === key;
                    return (
                      <button key={imp} data-testid={`heatmap-cell-${l}-${imp}`}
                        onClick={() => setCellFilter(active ? null : count ? key : cellFilter)}
                        className={`w-12 h-12 border border-white/60 flex items-center justify-center text-sm font-bold transition-transform ${cellColor(l * imp)} ${active ? "ring-2 ring-[#1E6FE8] scale-105" : ""} ${count ? "cursor-pointer hover:scale-105" : "opacity-60"}`}>
                        {count || ""}
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className="flex ml-5 mt-1">
                {[1, 2, 3, 4, 5].map((i) => <span key={i} className="w-12 text-center text-[10px] text-muted-foreground">{i}</span>)}
              </div>
              <div className="ml-5 text-[9px] uppercase tracking-widest text-muted-foreground text-center mt-0.5">Impact →</div>
            </div>
          </div>
          {cellFilter && (
            <button data-testid="heatmap-clear-filter" onClick={() => setCellFilter(null)}
              className="mt-3 inline-flex items-center gap-1 text-xs text-[#1E6FE8] underline"><X className="h-3 w-3" /> Clear cell filter</button>
          )}
        </div>

        <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-220px)]">
          <table className="w-full data-table text-sm">
            <thead><tr><th>Description</th><th>Type</th><th>Impact</th><th>Likelihood</th><th>Score</th><th>Owner</th><th>Status</th><th>CR</th><th className="w-24" /></tr></thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={9} className="text-center text-muted-foreground py-8">No entries{cellFilter ? " in this cell" : ""}.</td></tr>}
              {visible.map((item) => {
                const l = score(item, "likelihoodScore", "likelihood"), imp = score(item, "impactScore", "impact");
                return (
                  <tr key={item.id} data-testid="risk-issue-row">
                    <td className="max-w-md">{item.description}</td>
                    <td><StatusBadge value={item.type} tone="blue" /></td>
                    <td><StatusBadge value={item.impact} /></td>
                    <td><StatusBadge value={item.likelihood} /></td>
                    <td><span className={`inline-flex items-center justify-center w-8 h-6 text-xs font-bold ${cellColor(l * imp)}`}>{l * imp}</span></td>
                    <td className="whitespace-nowrap">{item.owner || "—"}</td>
                    <td><StatusBadge value={item.status} /></td>
                    <td className="whitespace-nowrap">
                      {item.linkedCrNumber
                        ? <Link to={`/projects/${projectId}/changes`} data-testid="linked-cr-badge" className="text-xs font-mono2 text-[#1E6FE8] underline">{item.linkedCrNumber}</Link>
                        : item.type === "Issue"
                          ? <button data-testid="convert-to-cr-btn" onClick={() => convert(item)} title="Promote to Change Request"
                              className="inline-flex items-center gap-1 text-xs text-[#1E6FE8] hover:underline"><GitPullRequestArrow className="h-3.5 w-3.5" /> → CR</button>
                          : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="whitespace-nowrap">
                      <button data-testid="edit-risk-issue-btn" onClick={() => { setEditing(item); setForm(item); setOpen(true); }} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid="delete-risk-issue-btn" onClick={() => remove(item)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} — Risk / Issue</DialogTitle></DialogHeader>
          <ArtifactForm fields={config.fields} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-risk-issue-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
